/**
 * THE FIRST DAY, ON THE DESK ITSELF.
 *
 * Anthony, 2026-10-10. Six of twelve approved shops signed in once and
 * never came back. What they saw was a desk counting nothing, with
 * "Nothing is stuck anywhere" and a trophy, and nothing saying what to
 * press first. The emails that say it arrive a day later.
 *
 * So until a shop has done the three things a shop needs on day one, the
 * desk opens with them, each one a link to the screen that does it, and
 * ticks them off from the shop's own rows (GET /api/onboarding, the same
 * reading the drip uses). When all three are done the card is gone.
 *
 * Under them, for the owner who signed up, one optional ask: the setup
 * steps by text. The button is the consent. The words beside it are the
 * words stored with it (IN_APP_CONSENT_TEXT in server/routes/onboarding.ts,
 * sent back by the server so the two cannot drift).
 */

import { db, api } from "../data/client.ts";
import { esc } from "../ui/dom.ts";

type Answer = {
  show: boolean; fresh?: boolean; video?: string;
  steps?: Array<{ id: string; label: string; why: string; href: string; done: boolean }>;
  sms?: { ask: boolean; phone?: string | null; words?: string };
};

async function bearer(): Promise<string> {
  const sb = await db();
  const { data: { session } } = await sb.auth.getSession();
  return session?.access_token ?? "";
}

/**
 * Draw the card into `id`. Resolves true when the shop has no customer
 * yet, so the desk can stop handing out trophies for an empty shop.
 * Any failure draws nothing and resolves false: the desk as it was.
 */
export async function firstDay(id: string): Promise<boolean> {
  let a: Answer;
  try {
    const r = await fetch(api("/api/onboarding"), { headers: { Authorization: `Bearer ${await bearer()}` } });
    if (!r.ok) return false;
    a = await r.json() as Answer;
  } catch { return false; }
  const el = document.getElementById(id);
  if (!el || !a.show || !a.steps) return Boolean(a.fresh);

  const left = a.steps.filter((s) => !s.done).length;
  el.innerHTML = `<div class="card pad firstday">
      <h2>Get your shop running</h2>
      <p class="firstday-sub">${left === a.steps.length
        ? `${(["Three", "Four", "Five", "Six"])[a.steps.length - 3] ?? a.steps.length} things, about ten minutes each. Start with the first one.`
        : `${left} to go. They tick themselves off as you do them.`}</p>
      <ol class="firstday-steps">${a.steps.map((s) => `
        <li class="${s.done ? "done" : ""}">
          <span class="firstday-tick" aria-hidden="true">${s.done ? "&#10003;" : ""}</span>
          <div>${s.done
            ? `<b>${esc(s.label)}</b><span>Done.</span>`
            : `<a href="${esc(s.href)}"${/^https?:/.test(s.href) ? ` target="_blank" rel="noopener"` : ""}><b>${esc(s.label)}</b></a><span>${esc(s.why)}</span>`}</div>
        </li>`).join("")}</ol>
      ${a.video ? `<p class="firstday-video"><a href="${esc(a.video)}" target="_blank" rel="noopener">
        Rather see the whole job at once? First call to paid, about 30 minutes</a></p>` : ""}
      ${a.sms?.ask ? `<div class="firstday-sms" id="fd-sms">
        <label class="firstday-field"><span>Get the next steps by text</span>
          <input id="fd-phone" type="tel" inputmode="tel" autocomplete="tel" value="${esc(a.sms.phone ?? "")}"
            placeholder="Your mobile number"></label>
        <button class="btn sm" id="fd-sms-go" type="button">Text me the setup steps</button>
        <p class="firstday-fine">${esc((a.sms.words ?? "").replace(/^Text me the setup steps\.\s*/, ""))}
          See our <a href="/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>
          and <a href="/terms.html" target="_blank" rel="noopener">Terms</a>.</p>
        <div class="msg-slot" id="fd-sms-msg"></div>
      </div>` : ""}
    </div>`;

  document.getElementById("fd-sms-go")?.addEventListener("click", async () => {
    const btn = document.getElementById("fd-sms-go") as HTMLButtonElement | null;
    const msg = document.getElementById("fd-sms-msg");
    const phone = (document.getElementById("fd-phone") as HTMLInputElement | null)?.value ?? "";
    if (btn) btn.disabled = true;
    try {
      const r = await fetch(api("/api/onboarding/sms-consent"), {
        method: "POST",
        headers: { Authorization: `Bearer ${await bearer()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ phone, agreed: true }),
      });
      const b = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
      if (r.ok && b.ok) {
        const box = document.getElementById("fd-sms");
        if (box) box.innerHTML = `<p class="firstday-fine">Done. The next step comes by text as well as email. Reply STOP any time.</p>`;
        return;
      }
      if (msg) msg.innerHTML = `<div class="msg bad">${esc(b.error ?? "That did not save. Try again in a minute.")}</div>`;
    } catch {
      if (msg) msg.innerHTML = `<div class="msg bad">That did not save. Try again in a minute.</div>`;
    }
    if (btn) btn.disabled = false;
  });
  return Boolean(a.fresh);
}
